import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

export default function JiraImport() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');

  // JIRA connection state
  const [isConnected, setIsConnected] = useState(false);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectName, setProjectName] = useState('');

  // Issue types per selected project
  const [issueTypes, setIssueTypes] = useState([]);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchMode, setSearchMode] = useState('simple'); // 'simple' | 'jql'
  const [searchProjectKey, setSearchProjectKey] = useState('');
  const [searchIssueType, setSearchIssueType] = useState('');
  const [jqlQuery, setJqlQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // ── Import state ──────────────────────────────────────────────────────────
  const [importProjectKey, setImportProjectKey] = useState('');
  const [importIssueType, setImportIssueType] = useState('');
  const [importIssueTypes, setImportIssueTypes] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    checkConnectionAndLoad();
  }, []);

  useEffect(() => {
    if (projectId) loadZipqaProjectName();
  }, [projectId]);

  // When search project changes, update issue types for the search panel
  useEffect(() => {
    const p = jiraProjects.find(j => j.key === searchProjectKey);
    setIssueTypes(p?.issueTypes || []);
    setSearchIssueType('');
  }, [searchProjectKey, jiraProjects]);

  // When import project changes, update issue types for the import panel
  useEffect(() => {
    const p = jiraProjects.find(j => j.key === importProjectKey);
    setImportIssueTypes(p?.issueTypes || []);
    setImportIssueType('');
  }, [importProjectKey, jiraProjects]);

  async function checkConnectionAndLoad() {
    setLoadingProjects(true);
    try {
      const res = await api.get('/jira/projects');
      setJiraProjects(res.data.data || []);
      setIsConnected(true);
    } catch {
      setIsConnected(false);
    } finally {
      setLoadingProjects(false);
    }
  }

  async function loadZipqaProjectName() {
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProjectName(res.data?.name || res.data?.data?.name || '');
    } catch { /* ignore */ }
  }

  async function runSearch() {
    let jql = '';
    if (searchMode === 'jql') {
      if (!jqlQuery.trim()) { setSearchError('Enter a JQL query.'); return; }
      jql = jqlQuery.trim();
    } else {
      if (!searchProjectKey) { setSearchError('Select a JIRA project to search.'); return; }
      jql = `project = "${searchProjectKey}"`;
      if (searchIssueType) jql += ` AND issuetype = "${searchIssueType}"`;
      jql += ' ORDER BY updated DESC';
    }

    setSearching(true);
    setSearchError('');
    setSearchResults([]);
    try {
      const res = await api.get('/jira/issues', { params: { jql, maxResults: 50 } });
      setSearchResults(res.data.data || []);
      if ((res.data.data || []).length === 0) setSearchError('No issues matched.');
    } catch (err) {
      setSearchError(err.response?.data?.error || err.message);
    } finally {
      setSearching(false);
    }
  }

  async function importIssues() {
    if (!projectId) { setImportError('No ZipQA project selected. Open this page from a project.'); return; }
    if (!importProjectKey) { setImportError('Select a JIRA project to import from.'); return; }
    setImporting(true);
    setImportResult(null);
    setImportError('');
    try {
      const res = await api.post('/jira/requirements/import-issues', {
        projectId,
        jiraProjectKey: importProjectKey,
        issueType: importIssueType || undefined,
      });
      setImportResult(res.data);
    } catch (err) {
      setImportError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  }

  function getPriorityIcon(priority) {
    if (!priority) return '⬜';
    const p = priority.toLowerCase();
    if (p === 'blocker' || p === 'critical') return '🔴';
    if (p === 'high' || p === 'major') return '🟠';
    if (p === 'medium' || p === 'normal') return '🟡';
    return '🟢';
  }

  function getStatusBadge(status) {
    if (!status) return <span className="badge bg-secondary">—</span>;
    const s = status.toLowerCase();
    if (s.includes('done') || s.includes('closed') || s.includes('resolved'))
      return <span className="badge bg-success">{status}</span>;
    if (s.includes('progress') || s.includes('review'))
      return <span className="badge bg-primary">{status}</span>;
    if (s.includes('blocked')) return <span className="badge bg-danger">{status}</span>;
    return <span className="badge bg-secondary">{status}</span>;
  }

  // ── Not connected ─────────────────────────────────────────────────────────
  if (!loadingProjects && !isConnected) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">⬇️ JIRA Import &amp; Search</h1>
            <p className="page-subtitle">Search and import JIRA issues as requirements.</p>
          </div>
        </div>
        <div className="container-fluid">
          <div className="empty-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔗</div>
            <h5>JIRA not connected</h5>
            <p className="text-muted">Configure your JIRA credentials first.</p>
            <Link to="/jira" className="btn btn-primary">Go to JIRA Settings</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <nav aria-label="breadcrumb" style={{ marginBottom: 4 }}>
            <ol className="breadcrumb mb-0" style={{ fontSize: 13 }}>
              {projectId && projectName ? (
                <>
                  <li className="breadcrumb-item"><Link to="/projects">Projects</Link></li>
                  <li className="breadcrumb-item"><Link to={`/projects/${projectId}`}>{projectName}</Link></li>
                  <li className="breadcrumb-item active">JIRA Import</li>
                </>
              ) : (
                <>
                  <li className="breadcrumb-item"><Link to="/jira">JIRA Settings</Link></li>
                  <li className="breadcrumb-item active">Import &amp; Search</li>
                </>
              )}
            </ol>
          </nav>
          <h1 className="page-title">⬇️ JIRA Import &amp; Search</h1>
          <p className="page-subtitle">
            Search JIRA issues and bulk-import them as requirements
            {projectName ? ` into ${projectName}` : ''}.
          </p>
        </div>
        <div className="d-flex gap-2 align-items-start">
          <Link to="/jira" className="btn btn-outline-secondary btn-sm">⚙️ JIRA Settings</Link>
        </div>
      </div>

      <div className="container-fluid">
        {loadingProjects && (
          <div className="zq-spinner-wrap">
            <span className="spinner-border" style={{ color: 'var(--zq-primary)' }} />
            <span style={{ fontSize: 13, color: 'var(--zq-text-muted)' }}>Loading JIRA projects…</span>
          </div>
        )}

        {!loadingProjects && (
          <>
            {/* ── Import section ── */}
            <div className="card mb-4">
              <div className="card-header">
                <strong>Import Issues as Requirements</strong>
              </div>
              <div className="card-body">
                {!projectId && (
                  <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                    ⚠️ No ZipQA project selected. Open this page from a project to enable import.
                  </div>
                )}
                <p style={{ fontSize: 13, color: 'var(--zq-text-secondary)', marginBottom: 16 }}>
                  Pull issues from a JIRA project into ZipQA as requirements. Already-imported issues are skipped automatically.
                </p>

                {importError && (
                  <div className="alert alert-danger alert-dismissible">
                    {importError}
                    <button className="btn-close" onClick={() => setImportError('')} />
                  </div>
                )}
                {importResult && (
                  <div className="alert alert-success">
                    ✅ Imported <strong>{importResult.imported}</strong> requirement(s).
                    {importResult.skipped > 0 && (
                      <span style={{ color: 'var(--zq-text-muted)' }}> ({importResult.skipped} already existed, skipped)</span>
                    )}
                  </div>
                )}

                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-4">
                    <label className="form-label">JIRA Project *</label>
                    {jiraProjects.length > 0 ? (
                      <select
                        className="form-select"
                        value={importProjectKey}
                        onChange={e => setImportProjectKey(e.target.value)}
                      >
                        <option value="">— Select project —</option>
                        {jiraProjects.map(p => (
                          <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. PROJ"
                        value={importProjectKey}
                        onChange={e => setImportProjectKey(e.target.value.toUpperCase())}
                      />
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Issue Type <small className="text-muted">(optional — imports all if blank)</small></label>
                    {importIssueTypes.length > 0 ? (
                      <select
                        className="form-select"
                        value={importIssueType}
                        onChange={e => setImportIssueType(e.target.value)}
                      >
                        <option value="">— All types —</option>
                        {importIssueTypes.map(t => (
                          <option key={t.id} value={t.name}>{t.name}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. Epic, Story, Bug"
                        value={importIssueType}
                        onChange={e => setImportIssueType(e.target.value)}
                        disabled={!importProjectKey}
                      />
                    )}
                  </div>
                  <div className="col-md-4">
                    <button
                      className="btn btn-success w-100"
                      onClick={importIssues}
                      disabled={importing || !importProjectKey || !projectId}
                    >
                      {importing ? (
                        <><span className="spinner-border spinner-border-sm me-2" />Importing…</>
                      ) : (
                        '⬇️ Import Issues'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Search section ── */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>Search JIRA Issues</strong>
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn ${searchMode === 'simple' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setSearchMode('simple')}
                  >Simple</button>
                  <button
                    type="button"
                    className={`btn ${searchMode === 'jql' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setSearchMode('jql')}
                  >JQL</button>
                </div>
              </div>
              <div className="card-body">
                {searchMode === 'simple' ? (
                  <div className="row g-3 mb-3">
                    <div className="col-md-5">
                      <label className="form-label">JIRA Project</label>
                      {jiraProjects.length > 0 ? (
                        <select
                          className="form-select"
                          value={searchProjectKey}
                          onChange={e => setSearchProjectKey(e.target.value)}
                        >
                          <option value="">— All projects —</option>
                          {jiraProjects.map(p => (
                            <option key={p.key} value={p.key}>{p.key} – {p.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. PROJ"
                          value={searchProjectKey}
                          onChange={e => setSearchProjectKey(e.target.value.toUpperCase())}
                        />
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Issue Type</label>
                      {issueTypes.length > 0 ? (
                        <select
                          className="form-select"
                          value={searchIssueType}
                          onChange={e => setSearchIssueType(e.target.value)}
                        >
                          <option value="">— All types —</option>
                          {issueTypes.map(t => (
                            <option key={t.id} value={t.name}>{t.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g. Epic, Story"
                          value={searchIssueType}
                          onChange={e => setSearchIssueType(e.target.value)}
                          disabled={!searchProjectKey}
                        />
                      )}
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                      <button
                        className="btn btn-outline-primary w-100"
                        onClick={runSearch}
                        disabled={searching}
                      >
                        {searching ? <span className="spinner-border spinner-border-sm" /> : '🔍 Search'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="input-group mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder='e.g. project = "PROJ" AND issuetype = Epic ORDER BY updated DESC'
                      value={jqlQuery}
                      onChange={e => setJqlQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && runSearch()}
                    />
                    <button
                      className="btn btn-outline-primary"
                      onClick={runSearch}
                      disabled={searching}
                    >
                      {searching ? <span className="spinner-border spinner-border-sm" /> : 'Search'}
                    </button>
                  </div>
                )}

                {searchError && <div className="alert alert-warning py-2">{searchError}</div>}

                {searchResults.length > 0 && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--zq-text-muted)', marginBottom: 8 }}>
                      Showing {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </div>
                    <div className="table-responsive">
                      <table className="table table-hover" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Key</th>
                            <th>Summary</th>
                            <th>Type</th>
                            <th>Priority</th>
                            <th>Status</th>
                            <th>Assignee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map(issue => (
                            <tr key={issue.issueKey}>
                              <td>
                                <a
                                  href={issue.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontWeight: 600 }}
                                >
                                  {issue.issueKey}
                                </a>
                              </td>
                              <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {issue.summary}
                              </td>
                              <td>
                                <span className="badge" style={{ background: 'var(--zq-border-light)', color: 'var(--zq-text-secondary)' }}>
                                  {issue.issueType}
                                </span>
                              </td>
                              <td>{getPriorityIcon(issue.priority)} {issue.priority}</td>
                              <td>{getStatusBadge(issue.status)}</td>
                              <td><small>{issue.assignee || '—'}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
